import moment from 'moment';
import { StockSearchType } from '../models/searchFilter';
import { fromTimeZone } from '../services/timeZone';

export interface BasicSearchOptions {
  accountId?: number;
  take?: number;
  skip?: number;
  wrapWith: {
    totalCount: boolean;
  };
}

export interface GridSearchOptions extends BasicSearchOptions {
  filters: any;
  sortModel: any[];
}

/**
 * Gets the default SIM search options.
 * @returns {BasicSearchOptions} The default basic SIM search options.
 */
export type SimSearchOptions = GridSearchOptions;

export interface SimSubmissionSearchOptions extends SimSearchOptions {
  companyId?: number;
  isScrollDown: boolean;
  searchTerm?: string;
  searchType: StockSearchType;
}

/**
 * Gets the default Spend Cap search options.
 * @returns {BasicSearchOptions} The default basic Spend Cap search options.
 */
export interface SpendCapSearchOptions extends BasicSearchOptions {
  filters: any;
  sortModel: any[];
  fullExport: Boolean;
}

/**
 * Gets the default standard search options.
 * @returns {OptionalSearchOptions} The default standard search options
 */
export interface StandardSearchOptions {
  accountId?: number;
  searchTerm: string;
  take: number;
  skip: number;
  wrapWith: {
    totalCount: boolean;
  };
}


/**
 * Gets the default optional search options.
 * @returns {OptionalSearchOptions} The default optional search options
 */
export interface OptionalSearchOptions {
  accountId?: number;
  searchTerm?: string;
  take?: number;
  skip?: number;
  wrapWith: {
    totalCount: boolean;
  };
}

/**
 * Gets the default SIM search options
 * @returns {SimSearchOptions} The default SIM search options
 */
export const defaultSimSearchOptions: SimSearchOptions = {
  filters: {},
  sortModel: [],
  wrapWith: {
    totalCount: false,
  },
};

/**
 * Gets the default search options
 * @returns {StandardSearchOptions} The defualt search options
 */
export const defaultSearchOptions: StandardSearchOptions = {
  searchTerm: '',
  skip: 0,
  take: 50,
  wrapWith: {
    totalCount: false,
  },
};

/**
 * Gets the default Spend Cap search options
 * @returns {SpendCapSearchOptions} The default Spend Cap search options
 */
export const defaultSpendCapSearchOptions: SpendCapSearchOptions = {
  filters: {},
  sortModel: [],
  take: undefined,
  fullExport: false,
  wrapWith: {
    totalCount: false,
  },
};


/**
 * Tries to parse a string as an integer.
 * @param str The string
 * @param defaultValue The detault value.
 * @returns The parsed value, or the default value if it fails to parse.
 */
export function tryParseInt(str: any, defaultValue: number) {
  let retValue = defaultValue;
  if (str && str.length > 0) {
    if (!isNaN(str)) {
      retValue = parseInt(str, 10);
    }
  }

  return retValue;
}

/**
 * Gets the date at midnight in the specified number of days time
 * @param days The number of days time
 * @returns The date at midnight in the specified number of days time
 */
export function getDaysTime(days: number) {
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + days);
  const tomorrowMidnight = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());
  return tomorrowMidnight;
}

function getUtcDate() : Date {
  const date = new Date();
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds());
}

/**
 * Gets the date at midnight in the specified number of days time
 * @param days The number of days time
 * @returns The date at midnight in the specified number of days time
 */
export function getUtcDaysTime(days: number) {
  const tomorrow = getUtcDate();
  tomorrow.setDate(getUtcDate().getDate() + days);
  return new Date(Date.UTC(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate()));
}

/**
 * Gets the start of the current month.
 * @param timeZone The timezone to get it in.
 */
export function getStartOfCurrentMonth(timeZone: string) {
  const today = new Date();
  return fromTimeZone(timeZone, today.getFullYear(), today.getMonth(), 1);
}

/**
 * Gets the date at midnight in the specified number of days time
 * @param days The number of days time
 * @returns The date at midnight in the specified number of days time
 */
export function getDaysAfter(date: Date, days: number) {
  const daysAfterDate = new Date();
  daysAfterDate.setDate(date.getDate() + days);
  const returnDate = new Date(daysAfterDate.getFullYear(), daysAfterDate.getMonth(), daysAfterDate.getDate());
  return returnDate;
}

/**
 * Gets the date at midnight in the specified number of months time
 * @param months The number of months time
 * @returns The date at midnight in the specified number of days time
 */
export function getMonthsTime(months: number) {
  const m = moment({ hour: 0, minute: 0, seconds: 0, milliseconds: 0 });
  if (months > 0) {
    m.add(months, 'months');
  } else {
    m.subtract(months * -1, 'months');
  }
  return m.toDate();
}

/**
 * Gets the date as a string in the format yyyy-MM-dd.
 * @param d The date to convert
 */
export function toDateString(d: Date) {
  return `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}-${('0' + d.getDate()).slice(-2)}`;
}

/**
 * Convert the first letter to upper case.
 * @param s The string to convert.
 * @returns The string with an uppercase first letter.
 */
export function toUpperCaseFirstLetter(s: string | null | undefined) {
  if (!s) {
    return s;
  }

  return s.charAt(0).toUpperCase() + s.slice(1);
}
